// Shared labels + types for Account Product Directives (Feature #40, M1).

import type { BadgeTone } from "@/lib/accountSignals"

export type DirectiveType =
  | "warning"
  | "delisting"
  | "flush_out"
  | "new_listing_approval"
  | "price_change"
  | "other"

export type DirectiveStatus =
  | "active"
  | "under_review"
  | "recovered"
  | "flushed_out"
  | "discontinued"
  | "cancelled"

export type LineStatus = "warning" | "cleared" | "discontinued"

export type Signatory = { name: string; role: string }

// Which sell-out metric a line's trajectory is plotted against (M2). Derived
// from the line's free-text threshold_unit on the server.
export type TrajectoryMetric = "value" | "qty"

export type TrajectoryPoint = {
  period_year: number
  period_month: number
  label: string
  value: number | null
  qty: number | null
}

// Per-line performance trajectory sourced from b2b_sell_out_transactions (M2).
export type LineTrajectory = {
  available: boolean
  product_code: string | null
  scope_column: string
  scope_value?: string
  unit_metric: TrajectoryMetric
  threshold_value: number | null
  threshold_unit: string | null
  window_months?: number
  series: TrajectoryPoint[]
}

// A STAR Master Product hit from directives#product_search (M2 SKU mapping).
export type StarProductResult = {
  product_code: string
  product_name: string | null
  brand_name: string | null
  status: string | null
}

export type DirectiveLine = {
  id: number | null
  raw_product_description: string
  account_sku_code: string | null
  product_code: string | null
  product_dist_code: string | null
  metrics: Record<string, string>
  threshold_value: string | null
  threshold_unit: string | null
  line_status: LineStatus
  allowed_transitions?: LineStatus[]
  outcome: string | null
  resolved_on: string | null
  trajectory?: LineTrajectory | null
}

export type DirectiveSummary = {
  id: number
  account_id: number
  account_name: string | null
  directive_type: DirectiveType
  ref_no: string | null
  brand: string | null
  subject: string | null
  issued_on: string | null
  grace_deadline: string | null
  nearest_deadline: string | null
  status: DirectiveStatus
  line_count: number
  has_document: boolean
}

export type DirectiveDetail = DirectiveSummary & {
  supplier_entity: string | null
  supplier_pic: string | null
  signatories: Signatory[]
  review_on: string | null
  flush_out_on: string | null
  discontinue_on: string | null
  clear_conditions: string | null
  clear_thresholds: Record<string, unknown>
  consequence_summary: string | null
  origin: string
  allowed_transitions: DirectiveStatus[]
  account_signal_id: number | null
  captured_by_name: string | null
  document_url: string | null
  document_filename: string | null
  lines: DirectiveLine[]
}

export type AccountOption = { id: number; name: string }

// Descriptive analytics (M4) — the directives#analytics response shape.
export type AnalyticsByAccountRow = {
  account_id: number
  account_name: string | null
  line_count: number
  directive_count: number
}

export type AnalyticsRevenueAccountRow = {
  account_id: number
  account_name: string | null
  amount: number
  sku_count: number
}

export type AnalyticsRevenueAtRisk = {
  total: number
  by_account: AnalyticsRevenueAccountRow[]
}

export type AnalyticsRecovery = {
  cleared: number
  discontinued: number
  warning: number
  resolved: number
  rate: number | null
}

export type AnalyticsRepeatOffender = {
  product_code: string
  directive_count: number
  account_count: number
  period_count: number
  sample_description: string | null
}

export type DirectiveAnalytics = {
  by_account: AnalyticsByAccountRow[]
  revenue_at_risk: AnalyticsRevenueAtRisk
  recovery: AnalyticsRecovery
  repeat_offenders: AnalyticsRepeatOffender[]
}

// A full-precision IDR amount, e.g. "Rp1.234.567" (for the at-risk totals/tables).
export function formatRp(value: number): string {
  return new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    maximumFractionDigits: 0,
  }).format(value)
}

// A 0..1 ratio as a percentage, e.g. "72,5%". Null → "—".
export function formatPercent(ratio: number | null): string {
  if (ratio == null) return "—"
  return new Intl.NumberFormat("id-ID", {
    style: "percent",
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(ratio)
}

export const DIRECTIVE_TYPE_LABELS: Record<DirectiveType, string> = {
  warning: "Peringatan",
  delisting: "Delisting",
  flush_out: "Flush-out",
  new_listing_approval: "Persetujuan Listing Baru",
  price_change: "Perubahan Harga",
  other: "Lainnya",
}

export const DIRECTIVE_STATUS_LABELS: Record<DirectiveStatus, string> = {
  active: "Aktif",
  under_review: "Sedang Ditinjau",
  recovered: "Pulih",
  flushed_out: "Flush-out",
  discontinued: "Dihentikan",
  cancelled: "Dibatalkan",
}

export const LINE_STATUS_LABELS: Record<LineStatus, string> = {
  warning: "Peringatan",
  cleared: "Pulih",
  discontinued: "Dihentikan",
}

export function directiveStatusTone(status: string): BadgeTone {
  switch (status) {
    case "active":
    case "under_review":
      return "danger"
    case "recovered":
      return "success"
    case "discontinued":
    case "flushed_out":
      return "muted"
    case "cancelled":
      return "neutral"
    default:
      return "neutral"
  }
}

export function lineStatusTone(status: string): BadgeTone {
  switch (status) {
    case "warning":
      return "danger"
    case "cleared":
      return "success"
    case "discontinued":
      return "muted"
    default:
      return "neutral"
  }
}

export function directiveTypeLabel(type: string): string {
  return DIRECTIVE_TYPE_LABELS[type as DirectiveType] ?? type
}

export function directiveStatusLabel(status: string): string {
  return DIRECTIVE_STATUS_LABELS[status as DirectiveStatus] ?? status
}

export function lineStatusLabel(status: string): string {
  return LINE_STATUS_LABELS[status as LineStatus] ?? status
}

export function formatDate(iso: string | null): string {
  if (!iso) return "—"
  return new Date(iso).toLocaleDateString("id-ID", {
    year: "numeric",
    month: "short",
    day: "numeric",
  })
}

// Compact IDR for the value (netto_wise) trajectory axis/tooltip, e.g. "Rp1,2 jt".
export function formatRpCompact(value: number): string {
  return new Intl.NumberFormat("id-ID", {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(value)
}

// Compact piece count for the qty (qty_total_pcs) trajectory.
export function formatQtyCompact(value: number): string {
  return new Intl.NumberFormat("id-ID", {
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(value)
}

export function trajectoryMetricLabel(metric: TrajectoryMetric): string {
  return metric === "qty" ? "Qty (pcs)" : "Netto (Rp)"
}

// Whole-day countdown to a deadline (negative = overdue), computed in the local
// timezone from a date-only ISO string. SSR-safe (no Date.now at module scope).
export function daysUntil(iso: string, now: Date = new Date()): number {
  const target = new Date(iso)
  const a = Date.UTC(target.getFullYear(), target.getMonth(), target.getDate())
  const b = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())
  return Math.round((a - b) / 86_400_000)
}

// A human countdown label + tone for a grace/consequence deadline (M3).
export function countdown(
  iso: string | null,
  now: Date = new Date()
): { label: string; tone: BadgeTone } | null {
  if (!iso) return null
  const days = daysUntil(iso, now)

  if (days < 0) return { label: `Terlambat ${Math.abs(days)} hari`, tone: "danger" }
  if (days === 0) return { label: "Tenggat hari ini", tone: "danger" }
  if (days <= 7) return { label: `${days} hari lagi`, tone: "danger" }
  if (days <= 30) return { label: `${days} hari lagi`, tone: "accent" }
  return { label: `${days} hari lagi`, tone: "muted" }
}
